<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-container">
      <div class="error-icon">❌</div>
      <h2 class="error-title">{{ errorTitle }}</h2>
      <p class="error-message">{{ errorMessage }}</p>

      <div v-if="showDetails" class="error-details">
        <details>
          <summary>Technical Details</summary>
          <pre>{{ errorDetails }}</pre>
        </details>
      </div>

      <div class="error-actions">
        <button @click="resetError" class="retry-button">
          Try Again
        </button>
        <button @click="goHome" class="home-button">
          Go Home
        </button>
      </div>
    </div>
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
/**
 * ErrorBoundary Component
 *
 * Global error boundary that catches unhandled errors in child components
 * and displays a user-friendly error message with recovery options.
 *
 * [I001.4] Implementation
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
import { ref, onErrorCaptured } from 'vue'
import { useRouter } from 'vue-router'

interface Props {
  showDetails?: boolean
  onError?: (error: Error) => void
}

const props = withDefaults(defineProps<Props>(), {
  showDetails: import.meta.env.DEV, // Only show details in development
  onError: undefined,
})

const router = useRouter()

// Error state
const hasError = ref(false)
const errorTitle = ref('Something went wrong')
const errorMessage = ref('An unexpected error occurred. Please try again.')
const errorDetails = ref('')

// Error capture hook
onErrorCaptured((error: Error, _instance, info) => {
  // Capture error details
  hasError.value = true
  errorTitle.value = 'Application Error'
  errorMessage.value = error.message || 'An unexpected error occurred.'
  errorDetails.value = `Error: ${error.message}\n\nStack: ${error.stack}\n\nComponent Info: ${info}`

  // Log error to console
  console.error('[ErrorBoundary] Caught error:', {
    error,
    message: error.message,
    stack: error.stack,
    info,
  })

  // Call custom error handler if provided
  if (props.onError) {
    props.onError(error)
  }

  // Prevent error from propagating
  return false
})

// Reset error state and retry
const resetError = () => {
  hasError.value = false
  errorTitle.value = ''
  errorMessage.value = ''
  errorDetails.value = ''

  // Reload the page to reset state
  window.location.reload()
}

// Navigate to home page
const goHome = () => {
  hasError.value = false
  errorTitle.value = ''
  errorMessage.value = ''
  errorDetails.value = ''

  router.push('/')
}
</script>

<style scoped>
.error-boundary {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.error-container {
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  text-align: center;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 24px;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-title {
  font-size: 1.8rem;
  color: #d32f2f;
  margin: 0 0 16px 0;
  font-weight: 700;
}

.error-message {
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.error-details {
  margin: 24px 0;
  text-align: left;
}

.error-details summary {
  cursor: pointer;
  font-weight: 600;
  color: #333;
  padding: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  user-select: none;
}

.error-details summary:hover {
  background: #e0e0e0;
}

.error-details pre {
  margin-top: 12px;
  padding: 16px;
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.retry-button,
.home-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.retry-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.home-button {
  background: #f5f5f5;
  color: #333;
}

.home-button:hover {
  background: #e0e0e0;
  transform: translateY(-2px);
}

.retry-button:active,
.home-button:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .error-container {
    padding: 32px 24px;
  }

  .error-title {
    font-size: 1.5rem;
  }

  .error-message {
    font-size: 1rem;
  }

  .error-actions {
    flex-direction: column;
  }

  .retry-button,
  .home-button {
    width: 100%;
  }
}
</style>
